import {
  FileExcelOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileZipOutlined,
} from '@ant-design/icons'
import { RolodexDocumentEntry } from 'features/files/types'
import isNil from 'lodash/isNil'
import CantileverTheme from 'styles/cantileverTheme'

// https://developer.mozilla.org/en-US/docs/Web/Media/Formats/Image_types
const IMAGE_TYPES = [
  'png',
  'jpg',
  'jpeg',
  'tiff',
  'jfif',
  'pjpeg',
  'pjp',
  'tif',
  'bmp',
  'gif',
  'svg',
  'webp',
]

// Excel and CSV file types
const EXCEL_CSV_TYPES = ['xlsx', 'xls', 'csv', 'tsv']

/**
 * Shared styles for file type icons
 */
const FILE_ICON_STYLES = {
  fontSize: 16,
  width: 16,
  height: 16,
  flexShrink: 0,
}

/**
 * File type to icon mapping
 */
export const FILE_TYPE_ICONS: Readonly<Record<string, React.ReactNode>> = {
  // PDF files
  pdf: <FilePdfOutlined style={{ ...FILE_ICON_STYLES, color: '#ff4d4f' }} />,

  // Excel and CSV files
  xlsx: <FileExcelOutlined style={{ ...FILE_ICON_STYLES, color: '#52c41a' }} />,
  xls: <FileExcelOutlined style={{ ...FILE_ICON_STYLES, color: '#52c41a' }} />,
  csv: <FileExcelOutlined style={{ ...FILE_ICON_STYLES, color: '#52c41a' }} />,
  tsv: <FileExcelOutlined style={{ ...FILE_ICON_STYLES, color: '#52c41a' }} />,

  // Text files
  txt: (
    <FileTextOutlined
      style={{
        ...FILE_ICON_STYLES,
        color: CantileverTheme.colors.grayscale[500],
      }}
    />
  ),
  json: (
    <FileTextOutlined
      style={{
        ...FILE_ICON_STYLES,
        color: CantileverTheme.colors.grayscale[500],
      }}
    />
  ),
  xml: (
    <FileTextOutlined
      style={{
        ...FILE_ICON_STYLES,
        color: CantileverTheme.colors.grayscale[500],
      }}
    />
  ),

  // Image files
  jpeg: <FileImageOutlined style={{ ...FILE_ICON_STYLES, color: '#52c41a' }} />,
  jpg: <FileImageOutlined style={{ ...FILE_ICON_STYLES, color: '#52c41a' }} />,
  png: <FileImageOutlined style={{ ...FILE_ICON_STYLES, color: '#52c41a' }} />,
  gif: <FileImageOutlined style={{ ...FILE_ICON_STYLES, color: '#52c41a' }} />,
  bmp: <FileImageOutlined style={{ ...FILE_ICON_STYLES, color: '#52c41a' }} />,
  tiff: <FileImageOutlined style={{ ...FILE_ICON_STYLES, color: '#52c41a' }} />,
  webp: <FileImageOutlined style={{ ...FILE_ICON_STYLES, color: '#52c41a' }} />,

  // Archive files
  zip: <FileZipOutlined style={{ ...FILE_ICON_STYLES, color: '#faad14' }} />,
  tar: <FileZipOutlined style={{ ...FILE_ICON_STYLES, color: '#faad14' }} />,
  gz: <FileZipOutlined style={{ ...FILE_ICON_STYLES, color: '#faad14' }} />,
}

/**
 * Utility function to check if a document is a PDF
 */
export const isPdfDocument = (document: RolodexDocumentEntry | null): boolean =>
  !isNil(document) && document.file_type?.toLowerCase() === 'pdf'

/**
 * Utility function to check if a document is an image
 */
export const isImageDocument = (
  document: RolodexDocumentEntry | null
): boolean => {
  if (isNil(document)) return false

  // Check by file_type field
  const hasImageType =
    !isNil(document.file_type) &&
    IMAGE_TYPES.includes(document.file_type.toLowerCase())

  // Fallback: Check by filename extension
  const hasImageExtension = Boolean(
    document.filename &&
    IMAGE_TYPES.some((ext) =>
      document.filename?.toLowerCase().endsWith(`.${ext}`)
    )
  )

  return hasImageType || hasImageExtension
}

/**
 * Utility function to check if a document is an Excel or CSV file
 */
export const isExcelCsvDocument = (
  document: RolodexDocumentEntry | null
): boolean => {
  if (isNil(document)) return false

  // Check by file_type field
  const hasExcelCsvType =
    !isNil(document.file_type) &&
    EXCEL_CSV_TYPES.includes(document.file_type.toLowerCase())

  // Fallback: Check by filename extension
  const hasExcelCsvExtension = Boolean(
    document.filename &&
    EXCEL_CSV_TYPES.some((ext) =>
      document.filename?.toLowerCase().endsWith(`.${ext}`)
    )
  )

  return hasExcelCsvType || hasExcelCsvExtension
}

/**
 * Get icon for file type
 */
export const getFileTypeIcon = (
  document: RolodexDocumentEntry
): React.ReactNode => {
  const fileType = document.file_type?.toLowerCase()

  // Check by file extension first (type field contains extension)
  if (fileType && FILE_TYPE_ICONS[fileType]) {
    return FILE_TYPE_ICONS[fileType]
  }

  // Default icon
  return (
    <FileOutlined
      style={{
        ...FILE_ICON_STYLES,
        color: CantileverTheme.colors.grayscale[400],
      }}
    />
  )
}
